import { useEffect, useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { photoUrl } from "@/lib/photos";

const FALLBACK_IMAGES = [
  "https://customer-assets.emergentagent.com/job_playas-veracruz/artifacts/9zic33cw_a8ddb780-66a3-4fbe-b8df-ff87c066511a.jpg",
  "https://customer-assets.emergentagent.com/job_playas-veracruz/artifacts/5mzc0tyz_6c580bb7-b105-4086-a8ca-5bc6850ae4d8.jpg",
  "https://customer-assets.emergentagent.com/job_playas-veracruz/artifacts/q8kmt15z_b698c33e-488d-4bac-b9e5-cb307bfaef50.jpg",
  "https://customer-assets.emergentagent.com/job_playas-veracruz/artifacts/lojfad7u_83fcf2b1-91d6-485d-a1ac-924d5333191c.jpg",
  "https://customer-assets.emergentagent.com/job_playas-veracruz/artifacts/1zw1i3j4_bfade5ce-db82-4802-acc6-9dd885c0b5ef.jpg",
  "https://images.unsplash.com/photo-1437622368342-7a3d40f6e6cd?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
];

const ICONS = [
  "fa-solid fa-shoe-prints",
  "fa-solid fa-cloud-sun",
  "fa-solid fa-fire-burner",
  "fa-solid fa-fire",
  "fa-solid fa-person-walking",
  "fa-solid fa-spider",
  "fa-solid fa-circle-dot",
  "fa-solid fa-water",
  "fa-solid fa-volleyball",
  "fa-solid fa-person-swimming",
  "fa-solid fa-child",
  "fa-solid fa-heart",
];

export default function Activities() {
  const { t } = useLang();
  const items = t("activities.items");
  const [uploaded, setUploaded] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/photos?property_id=home`);
        if (cancelled) return;
        setUploaded(data?.sections?.actividades || []);
      } catch {
        if (!cancelled) setUploaded([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const getImageFor = (i) => {
    if (uploaded && uploaded[i]) {
      return photoUrl(uploaded[i]);
    }
    return FALLBACK_IMAGES[i % FALLBACK_IMAGES.length];
  };

  const getAltFor = (i, fallback) => {
    return uploaded?.[i]?.seo_alt || fallback;
  };

  return (
    <section
      id="actividades"
      data-testid="activities-section"
      className="bg-stone-100 py-24 md:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16 items-end">
          <div className="lg:col-span-7">
            <p className="text-eyebrow text-[#E07A5F] mb-4">{t("activities.eyebrow")}</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stone-900">
              {t("activities.title")}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-stone-700 text-lg leading-relaxed">{t("activities.sub")}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {items.map((it, i) => (
            <div
              key={i}
              data-testid={`activity-${i}`}
              className="group bg-white border border-stone-200 hover:border-stone-400 transition-all overflow-hidden hover:-translate-y-1"
            >
              <div className="aspect-[4/3] overflow-hidden bg-stone-200 relative">
                <img
                  src={getImageFor(i)}
                  alt={getAltFor(i, it.t)}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
                />
                <div className="absolute top-3 left-3 w-9 h-9 bg-stone-50/90 backdrop-blur flex items-center justify-center">
                  <i className={`${ICONS[i % ICONS.length]} text-[#E07A5F] text-sm`} />
                </div>
              </div>
              <div className="p-5">
                <div className="text-eyebrow text-stone-400 mb-2">
                  0{i + 1} / 0{items.length}
                </div>
                <h3 className="font-serif-display text-xl md:text-2xl text-stone-900 leading-tight mb-2">
                  {it.t}
                </h3>
                <p className="text-sm text-stone-600 leading-relaxed">{it.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
